/* nativeBridge — single entry point the React app uses to talk to native
   capabilities. Falls back to the existing Web APIs whenever the app is
   running inside a regular browser / PWA (Capacitor.isNativePlatform()
   returns false). Keeping all native conditionals here means feature
   code in the rest of the app stays platform-agnostic. */

import { Capacitor } from "@capacitor/core";
import { PushNotifications } from "@capacitor/push-notifications";
import { App as CapApp } from "@capacitor/app";
import { StatusBar, Style } from "@capacitor/status-bar";
import { SplashScreen } from "@capacitor/splash-screen";
import { Keyboard } from "@capacitor/keyboard";
import { api } from "./api";

export const isNative = Capacitor.isNativePlatform();
export const platform = Capacitor.getPlatform(); // "ios" | "android" | "web"

/* ---------- Status bar + splash (cosmetic) ---------- */
export async function configureNativeChrome() {
  if (!isNative) return;
  try {
    await StatusBar.setStyle({ style: Style.Light }); // light icons on navy bg
    await StatusBar.setBackgroundColor({ color: "#0A192F" });
    await StatusBar.setOverlaysWebView({ overlay: true });
  } catch { /* iOS may refuse on older versions */ }
  try { await SplashScreen.hide(); } catch { /* noop */ }
  // Hide the iOS "form assistant" toolbar that floats above the keyboard
  // (the gray strip with up/down arrows and a Done ✓ checkmark). Looks
  // out of place in a chat UI — and we ship our own send button right
  // next to the input. Android has no equivalent so this is a no-op
  // there. The setting persists for the WebView's lifetime.
  if (platform === "ios") {
    try { await Keyboard.setAccessoryBarVisible({ isVisible: false }); } catch { /* noop */ }
    // WhatsApp-style keyboard slide: expose the live keyboard height as
    // a CSS variable `--keyboard-h` so the layout shell can shrink in
    // perfect lockstep with iOS's own animation curve. The `willShow`
    // / `willHide` events fire BEFORE the keyboard animates, with the
    // exact duration the OS will use, so updating CSS now lets the
    // browser interpolate the height change alongside the keyboard.
    try {
      const root = document.documentElement;
      root.style.setProperty("--keyboard-h", "0px");
      Keyboard.addListener("keyboardWillShow", (info) => {
        root.style.setProperty("--keyboard-h", `${info.keyboardHeight}px`);
        root.classList.add("keyboard-open");
      });
      Keyboard.addListener("keyboardWillHide", () => {
        root.style.setProperty("--keyboard-h", "0px");
        root.classList.remove("keyboard-open");
      });
    } catch { /* noop */ }
  }
}

/* ---------- Native push notifications (APNs + FCM via Capacitor) ----------

   Replaces the Web-Push subscription flow when running natively. The
   server endpoint and storage format stay identical so the same
   `/api/push/subscribe` handler accepts both — discriminated by the
   `kind` field ("web" | "native"). */
let pushReadyPromise = null;
export function ensureNativePush() {
  if (!isNative) return Promise.resolve(false);
  if (pushReadyPromise) return pushReadyPromise;

  pushReadyPromise = (async () => {
    try {
      const perm = await PushNotifications.requestPermissions();
      if (perm.receive !== "granted") return false;

      // Register with APNs/FCM. The native side returns a device token
      // in the `registration` event, which we POST to the backend.
      await new Promise((resolve, reject) => {
        const ok = PushNotifications.addListener("registration", async (token) => {
          try {
            await api.post("/push/subscribe-native", {
              kind: "native",
              platform, // "ios" or "android"
              token: token.value,
            });
            resolve(true);
          } catch (e) { reject(e); }
        });
        const err = PushNotifications.addListener("registrationError", (e) => reject(e));
        PushNotifications.register().catch(reject);
        // Best-effort timeout
        setTimeout(() => resolve(false), 12000);
        void ok; void err; // keep refs alive
      });

      // When the user taps a notification while the app is backgrounded,
      // Capacitor fires this event. We extract the call_id / link and
      // route via the same `lexiq:notif-click` postMessage that the SW
      // uses on web — CallContext already listens for it.
      PushNotifications.addListener("pushNotificationActionPerformed", (action) => {
        const data = action?.notification?.data || {};
        try {
          window.postMessage(
            {
              type: "lexiq:notif-click",
              kind: data.kind || null,
              link: data.link || "/notifications",
              call_id: data.call_id || null,
              action: action.actionId || null,
            },
            "*",
          );
        } catch { /* noop */ }
      });

      // Foreground delivery — let the app refresh / show a toast.
      PushNotifications.addListener("pushNotificationReceived", () => {
        try { window.dispatchEvent(new CustomEvent("lex:push-received")); } catch { /* noop */ }
      });

      return true;
    } catch { return false; }
  })();
  return pushReadyPromise;
}

/* ---------- Deep links (lexiq:// and Universal Links) ----------

   The native shell receives Universal Links / App Links / lexiq:// URLs
   here. We forward them to React Router via the same postMessage path
   the Service Worker uses, so all routing logic lives in one place. */
export function attachDeepLinkHandler() {
  if (!isNative) return () => {};
  const sub = CapApp.addListener("appUrlOpen", (event) => {
    try {
      const url = new URL(event.url);
      const path = url.pathname + (url.search || "") + (url.hash || "");
      window.postMessage(
        { type: "lexiq:notif-click", link: path || "/" },
        "*",
      );
    } catch { /* malformed URL */ }
  });
  return () => { try { sub.remove?.(); } catch { /* noop */ } };
}

/* ---------- One-shot init you can call from your App root ---------- */
export async function initNativeShell() {
  if (!isNative) return;
  await configureNativeChrome();
  attachDeepLinkHandler();
  // Push registration is opt-in — wire it once the user is signed in,
  // not at app launch, to avoid prompting too early.
}
